import React, { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { InputText as PrimeInputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { InputSwitch } from "primereact/inputswitch";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import { useNavigate } from "react-router-dom";
import useToast from "../../../../components/common/useToast";
import apiFunctions from "../../../../services/api-service";
import { AxiosError } from "axios";
import { errorHandler } from "../../../../utils/HelperFunctions";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";

const ApplicationSettingsPage: React.FC = () => {
    const navigate = useNavigate();
    const initialized = useRef(false);
    const { showToast }: any = useToast();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [version, setVersion] = useState("");
    const [versionName, setVersionName] = useState("");
    const [maintenanceMode, setMaintenanceMode] = useState(false);
    const [maintenanceMessage, setMaintenanceMessage] = useState("");
    const [maintenanceMessageGujarati, setMaintenanceMessageGujarati] = useState("");

    const getSettings = async () => {
        try {
            const response = await apiFunctions.get("/applicationsetting/");
            if (response?.data?.success) {
                const data = response.data.data || {};
                setVersion(data.version || "");
                setVersionName(data.versionName || "");
                setMaintenanceMode(!!data.maintenanceMode);
                setMaintenanceMessage(data.maintenanceMessage || "");
                setMaintenanceMessageGujarati(data.maintenanceMessageGujarati || "");
            } else {
                showToast({ severity: "error", summary: "Error", detail: response?.data?.message || "Failed to load settings", life: 3000 });
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!initialized.current) {
            initialized.current = true;
            getSettings();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleBack = () => {
        navigate("/admin/settings");
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!version.trim() || !versionName.trim()) {
            showToast({ severity: "warn", summary: "Warning", detail: "Version and Version Name are required", life: 3000 });
            return;
        }

        try {
            setSaving(true);
            const payload = {
                version: version.trim(),
                versionName: versionName.trim(),
                maintenanceMode,
                maintenanceMessage: maintenanceMessage.trim(),
                maintenanceMessageGujarati: maintenanceMessageGujarati.trim(),
            };

            const response = await apiFunctions.patch("/applicationsetting/", payload);

            if (response?.data?.success) {
                showToast({ severity: "success", summary: "Success", detail: response.data.message, life: 3000 });
            } else {
                showToast({ severity: "error", summary: "Error", detail: response?.data?.message || "Something went wrong", life: 3000 });
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <div className="back-button" onClick={handleBack}>
                <ArrowLeftIcon className="w-6 h-6 text-black-700" /><h3 className="m-0">Back</h3>
            </div>
            <div className="page-title">
                <h3>Application Settings</h3>
            </div>
            <form onSubmit={handleSubmit}>
                <Card className="grid form-layout">
                    {loading ? (
                        <>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12 md:col-span-6">
                                    <Skeleton width="60px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                                <div className="col-span-12 md:col-span-6">
                                    <Skeleton width="100px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="42px" />
                                </div>
                            </div>
                            <div className="grid grid-cols-12 gap-4" style={{ marginTop: "20px" }}>
                                <div className="col-span-12">
                                    <Skeleton width="140px" height="0.9rem" className="mb-2" />
                                    <Skeleton height="30px" />
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="grid grid-cols-12 gap-4">
                                <div className="col-span-12 md:col-span-6">
                                    <label className="form-label">Version *</label>
                                    <PrimeInputText
                                        type="text"
                                        value={version}
                                        onChange={(e) => setVersion(e.target.value)}
                                        placeholder="e.g. 1.2"
                                        className="w-full mt-1"
                                        autoFocus
                                    />
                                </div>
                                <div className="col-span-12 md:col-span-6">
                                    <label className="form-label">Version Name *</label>
                                    <PrimeInputText
                                        type="text"
                                        value={versionName}
                                        onChange={(e) => setVersionName(e.target.value)}
                                        placeholder="e.g. 1.2.0"
                                        className="w-full mt-1"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-12 gap-4" style={{ marginTop: "20px" }}>
                                <div className="col-span-12">
                                    <label className="form-label">Maintenance Mode (Mobile App Only)</label>
                                    <div className="flex items-center gap-2 mt-2">
                                        {/* shrink-0: without it, this flex row's neighboring text
                                            (longer for "ON" than "OFF") squeezes the switch's own
                                            width down to fit on a narrow screen instead of wrapping
                                            the text — flexbox shrinks any flex item with no shrink
                                            guard by default, and the switch had none, so its slider/
                                            knob rendered squashed/misaligned. Text wraps instead now. */}
                                        <InputSwitch checked={maintenanceMode} onChange={(e) => setMaintenanceMode(!!e.value)} className="shrink-0" />
                                        <span className="text-xs" style={{ color: "#4b5563" }}>
                                            {maintenanceMode ? "ON — mobile app will show the maintenance screen" : "OFF — mobile app works normally"}
                                        </span>
                                    </div>
                                    <p className="text-xs m-0 mt-2" style={{ color: "#9ca3af" }}>
                                        This only affects the mobile app. The website and admin panel keep running as usual.
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-12 gap-4" style={{ marginTop: "20px" }}>
                                <div className="col-span-12 md:col-span-6">
                                    <label className="form-label">Maintenance Message (English)</label>
                                    <InputTextarea
                                        value={maintenanceMessage}
                                        onChange={(e) => setMaintenanceMessage(e.target.value)}
                                        rows={3}
                                        placeholder="e.g. We'll be back shortly. Thanks for your patience!"
                                        className="w-full mt-1"
                                    />
                                </div>
                                <div className="col-span-12 md:col-span-6">
                                    <label className="form-label">Maintenance Message (ગુજરાતી)</label>
                                    <InputTextarea
                                        value={maintenanceMessageGujarati}
                                        onChange={(e) => setMaintenanceMessageGujarati(e.target.value)}
                                        rows={3}
                                        placeholder="દા.ત. અમે ટૂંક સમયમાં પાછા આવીશું. તમારી ધીરજ બદલ આભાર!"
                                        className="w-full mt-1"
                                    />
                                </div>
                            </div>

                            <div
                                className="flex justify-end gap-2"
                                style={{ width: "100%", marginTop: "24px", paddingTop: "20px", borderTop: "1px solid #e5e7eb" }}
                            >
                                <Button
                                    type="button"
                                    label="Cancel"
                                    severity="secondary"
                                    onClick={handleBack}
                                    disabled={saving}
                                />
                                <Button
                                    label="Save"
                                    className="prime-button btn-login"
                                    loading={saving}
                                    type="submit"
                                />
                            </div>
                        </>
                    )}
                </Card>
            </form>
        </>
    );
};

export default ApplicationSettingsPage;
